import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

import { AppShell } from './components/AppShell.js';
import { useRememberOfferFromAddress } from './components/OfferLine.js';
import { RequireAnonymous, RequireAuth, RequireOperator } from './components/RouteGuards.js';
import { ClientDetailPage } from './pages/ClientDetailPage.js';
import { ClientFormPage } from './pages/ClientFormPage.js';
import { ClientsPage } from './pages/ClientsPage.js';
import { CreditsPage } from './pages/CreditsPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { NewOrderPage } from './pages/NewOrderPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';
import { OrderDetailPage } from './pages/OrderDetailPage.js';
import { OrdersPage } from './pages/OrdersPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { SetPasswordPage } from './pages/SetPasswordPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SignInPage } from './pages/SignInPage.js';
import { SignUpPage } from './pages/SignUpPage.js';
import { SitesPage } from './pages/SitesPage.js';
import { PrivacyPage } from './pages/PrivacyPage.js';
import { TermsPage } from './pages/TermsPage.js';

/**
 * The operator surface is loaded on demand — candidate C-4.
 *
 * Agency users are the overwhelming majority and never open any of these
 * pages, so shipping the admin screens in the first-load bundle would make
 * every agency download an interface they cannot even reach. There is exactly
 * one operator account.
 */
const AdminOrdersPage = lazy(() =>
  import('./pages/admin/AdminOrdersPage.js').then((m) => ({ default: m.AdminOrdersPage })),
);
const AdminOrderDetailPage = lazy(() =>
  import('./pages/admin/AdminOrderDetailPage.js').then((m) => ({
    default: m.AdminOrderDetailPage,
  })),
);
const AdminAgenciesPage = lazy(() =>
  import('./pages/admin/AdminAgenciesPage.js').then((m) => ({ default: m.AdminAgenciesPage })),
);
const AdminAgencyDetailPage = lazy(() =>
  import('./pages/admin/AdminAgencyDetailPage.js').then((m) => ({
    default: m.AdminAgencyDetailPage,
  })),
);
const AdminSalesPage = lazy(() =>
  import('./pages/admin/AdminSalesPage.js').then((m) => ({ default: m.AdminSalesPage })),
);
const AdminReconciliationPage = lazy(() =>
  import('./pages/admin/AdminReconciliationPage.js').then((m) => ({
    default: m.AdminReconciliationPage,
  })),
);
const AdminCouponsPage = lazy(() =>
  import('./pages/admin/AdminCouponsPage.js').then((m) => ({ default: m.AdminCouponsPage })),
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage.js').then((m) => ({ default: m.ContactPage })),
);
const AdminInboxPage = lazy(() =>
  import('./pages/admin/AdminInboxPage.js').then((m) => ({ default: m.AdminInboxPage })),
);
const AdminPricingPage = lazy(() =>
  import('./pages/admin/AdminPricingPage.js').then((m) => ({ default: m.AdminPricingPage })),
);
const AdminRenewalsPage = lazy(() =>
  import('./pages/admin/AdminRenewalsPage.js').then((m) => ({ default: m.AdminRenewalsPage })),
);
const AdminEmailsPage = lazy(() =>
  import('./pages/admin/AdminEmailsPage.js').then((m) => ({ default: m.AdminEmailsPage })),
);
const AdminMetaPage = lazy(() =>
  import('./pages/admin/AdminMetaPage.js').then((m) => ({ default: m.AdminMetaPage })),
);

/** Wraps a signed-in page in the app chrome and the auth guard. */
function Protected({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <RequireAuth>
      <AppShell>{children}</AppShell>
    </RequireAuth>
  );
}

/**
 * `/clients/*` → `/projects/*` — ruling A-2.
 *
 * The rename is user-facing, and every bookmark, email link and browser history
 * entry an agency already has points at the old path. `replace` so the old URL
 * does not sit in the back button waiting to bounce them again.
 */
function RedirectToProject(): React.JSX.Element {
  const { clientProfileId } = useParams<{ clientProfileId: string }>();
  return <Navigate to={`/projects/${clientProfileId ?? ''}`} replace />;
}

function RedirectToProjectEdit(): React.JSX.Element {
  const { clientProfileId } = useParams<{ clientProfileId: string }>();
  return <Navigate to={`/projects/${clientProfileId ?? ''}/edit`} replace />;
}

/** Operator pages: same chrome, stricter guard, loaded on demand. */
function Operator({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <RequireOperator>
      <AppShell>
        <Suspense
          fallback={
            <p className="text-sm text-fg-muted" role="status">
              Loading…
            </p>
          }
        >
          {children}
        </Suspense>
      </AppShell>
    </RequireOperator>
  );
}

/** Throws while rendering. Reachable only in a development build; see its route. */
function CrashOnPurpose(): React.JSX.Element {
  throw new Error('A crash on purpose, from /dev/crash, to rehearse the operator alert.');
}

export function App(): React.JSX.Element {
  // C-133: a `?offer=` on any address is kept for the first Buy credits visit.
  useRememberOfferFromAddress();

  return (
    <Routes>
      <Route
        path="/sign-in"
        element={
          <RequireAnonymous>
            <SignInPage />
          </RequireAnonymous>
        }
      />
      <Route
        path="/sign-up"
        element={
          <RequireAnonymous>
            <SignUpPage />
          </RequireAnonymous>
        }
      />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/*
        D-14 Item 3.4. Deliberately outside `RequireAnonymous`, unlike sign-up:
        an operator who has just sent an invite and is checking the link should
        see the page rather than be bounced to the dashboard, and the token is
        the credential either way.
      */}
      <Route path="/set-password" element={<SetPasswordPage />} />

      {/* Ruling D-2: linked discreetly from every footer, signed in or out. */}
      <Route path="/terms" element={<TermsPage />} />

      {/*
        D-14 Item 8.3. Public for the same reason the Terms are: the sign-up
        line links to it, and somebody deciding whether to create an account
        needs to read it before they have one.
      */}
      <Route path="/privacy" element={<PrivacyPage />} />

      {/*
        C-32. **Development builds only**: a page that crashes on purpose, so the
        screen-crash report and the operator's alert can be rehearsed on the dev
        stack. `import.meta.env.DEV` is replaced with `false` in a production
        build, so this route is not in the bundle Cloudflare serves.
      */}
      {import.meta.env.DEV ? (
        <Route
          path="/dev/crash"
          element={
            /* Inside the signed-in app since WAIT-PL Q-1, so a crash here is
               reported with `signedIn: true`, as a real page's would be. */
            <Protected>
              <CrashOnPurpose />
            </Protected>
          }
        />
      ) : null}

      <Route
        path="/"
        element={
          <Protected>
            <DashboardPage />
          </Protected>
        }
      />
      <Route
        path="/credits"
        element={
          <Protected>
            <CreditsPage />
          </Protected>
        }
      />

      <Route
        path="/settings"
        element={
          <Protected>
            <SettingsPage />
          </Protected>
        }
      />

      <Route
        path="/projects"
        element={
          <Protected>
            <ClientsPage />
          </Protected>
        }
      />
      <Route
        path="/projects/new"
        element={
          <Protected>
            <ClientFormPage />
          </Protected>
        }
      />
      <Route
        path="/projects/:clientProfileId"
        element={
          <Protected>
            <ClientDetailPage />
          </Protected>
        }
      />
      <Route
        path="/projects/:clientProfileId/edit"
        element={
          <Protected>
            <ClientFormPage />
          </Protected>
        }
      />

      {/* The pre-rename paths, kept working. See RedirectToProject. */}
      <Route path="/clients" element={<Navigate to="/projects" replace />} />
      <Route path="/clients/new" element={<Navigate to="/projects/new" replace />} />
      <Route path="/clients/:clientProfileId" element={<RedirectToProject />} />
      <Route path="/clients/:clientProfileId/edit" element={<RedirectToProjectEdit />} />

      <Route
        path="/sites"
        element={
          <Protected>
            <SitesPage />
          </Protected>
        }
      />

      <Route
        path="/orders"
        element={
          <Protected>
            <OrdersPage />
          </Protected>
        }
      />
      <Route
        path="/orders/new"
        element={
          <Protected>
            <NewOrderPage />
          </Protected>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <Protected>
            <OrderDetailPage />
          </Protected>
        }
      />

      <Route
        path="/admin/orders"
        element={
          <Operator>
            <AdminOrdersPage />
          </Operator>
        }
      />
      <Route
        path="/admin/orders/:orderId"
        element={
          <Operator>
            <AdminOrderDetailPage />
          </Operator>
        }
      />
      <Route
        path="/admin/agencies"
        element={
          <Operator>
            <AdminAgenciesPage />
          </Operator>
        }
      />
      <Route
        path="/admin/agencies/:agencyId"
        element={
          <Operator>
            <AdminAgencyDetailPage />
          </Operator>
        }
      />
      <Route
        path="/admin/sales"
        element={
          <Operator>
            <AdminSalesPage />
          </Operator>
        }
      />
      <Route
        path="/admin/reconciliation"
        element={
          <Operator>
            <AdminReconciliationPage />
          </Operator>
        }
      />
      <Route
        path="/admin/coupons"
        element={
          <Operator>
            <AdminCouponsPage />
          </Operator>
        }
      />
      <Route
        path="/contact"
        element={
          <Protected>
            <ContactPage />
          </Protected>
        }
      />
      <Route
        path="/admin/inbox"
        element={
          <Operator>
            <AdminInboxPage />
          </Operator>
        }
      />
      <Route
        path="/admin/pricing"
        element={
          <Operator>
            <AdminPricingPage />
          </Operator>
        }
      />
      <Route
        path="/admin/renewals"
        element={
          <Operator>
            <AdminRenewalsPage />
          </Operator>
        }
      />
      <Route
        path="/admin/emails"
        element={
          <Operator>
            <AdminEmailsPage />
          </Operator>
        }
      />
      <Route
        path="/admin/meta"
        element={
          <Operator>
            <AdminMetaPage />
          </Operator>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
